import {
  Calendar,
  FileText,
  Home,
  LucideIcon,
  Package,
  User,
} from "lucide-react";
import React from "react";
import { Link, useLocation } from "react-router-dom";
import { useSidebar } from "~/providers/sidebar.provider";

interface NavItem {
  id: string;
  icon: LucideIcon;
  label: string;
  path: string;
}

const navItems: NavItem[] = [
  { id: "overview", icon: Home, label: "Overview", path: "/overview" },
  { id: "jobs", icon: Calendar, label: "Services", path: "/job" },
  { id: "inventory", icon: Package, label: "Inventory", path: "/inventory" },
  { id: "documents", icon: FileText, label: "Documents", path: "/documents" },
  // { id: "billing", icon: DollarSign, label: "Billing", path: "/billing" }, // Hidden temporarily
];

const Sidebar: React.FC = () => {
  const { isExpanded, isMobileOpen, isHovered, setIsHovered } = useSidebar();
  const location = useLocation();

  const getActiveTab = (): string => {
    const currentPath = location.pathname;
    if (currentPath.includes("overview")) return "overview";
    if (currentPath.includes("job")) return "jobs";
    if (currentPath.includes("inventory")) return "inventory";
    if (currentPath.includes("document")) return "documents";
    // if (currentPath.includes("billing")) return "billing"; // Hidden temporarily
    if (currentPath.includes("profile")) return "profile";
    return "overview";
  };

  const activeTab = getActiveTab();

  return (
    <aside
      className={`fixed mt-16 flex flex-col lg:mt-0 top-0 px-0 left-0 bg-[#fffff5] text-black h-screen transition-all duration-300 ease-in-out z-[60] border-r-2 border-black
        ${
          isExpanded || isMobileOpen
            ? "w-[290px]"
            : isHovered
            ? "w-[290px]"
            : "w-[90px]"
        }
        ${isMobileOpen ? "translate-x-0" : "-translate-x-full"}
        lg:translate-x-0`}
      onMouseEnter={() => !isExpanded && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div
        className={`py-6 px-4 flex ${
          !isExpanded && !isHovered ? "lg:justify-center" : "justify-start"
        }`}
      >
        <Link to="/overview" className="w-full">
          {isExpanded || isHovered || isMobileOpen ? (
            <div className="flex items-center gap-2">
              <img className="size-6" src="/logo.png" alt="Logo" />
              <span className="ml-3 text-md font-bold uppercase tracking-wide">
                Art Handler Collective
              </span>
            </div>
          ) : (
            <div className="flex justify-center">
              <img className="size-6" src="/logo.png" alt="Logo" />
            </div>
          )}
        </Link>
      </div>

      <div className="flex flex-col flex-1 overflow-y-auto duration-300 ease-linear no-scrollbar px-4">
        <nav className="mb-6 pt-12">
          <ul className="flex flex-col gap-2">
            {navItems.map((nav) => {
              const Icon = nav.icon;
              const isActive = activeTab === nav.id;

              return (
                <li key={nav.id}>
                  <Link
                    to={nav.path}
                    className={`flex items-center gap-3 px-3 py-3 font-bold text-sm uppercase tracking-wide transition-all border-2 border-black ${
                      isActive
                        ? "bg-black text-white"
                        : "bg-[#fffff5] text-black hover:bg-[#fffef5]"
                    }`}
                  >
                    <Icon size={20} />
                    {(isExpanded || isHovered || isMobileOpen) && (
                      <span>{nav.label}</span>
                    )}
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>
      </div>

      {/* Profile button at bottom */}
      <div className="pb-6 px-4 border-t-2 border-black pt-4">
        <Link
          to="/profile"
          className={`flex items-center gap-3 px-3 py-3 font-bold text-sm uppercase tracking-wide transition-all border-2 border-black ${
            activeTab === "profile"
              ? "bg-black text-white"
              : "bg-[#fffff5] text-black hover:bg-[#fffef5]"
          }`}
        >
          <User size={20} />
          {(isExpanded || isHovered || isMobileOpen) && (
            <span>Profile</span>
          )}
        </Link>
      </div>
    </aside>
  );
};

export default Sidebar;
